import { ErrorResponse } from '@aeris/contracts';
/**
 * O transporte, e mais nada.
 *
 * No site é same-origin. O servidor de desenvolvimento do Vite faz proxy de
 * `/api` e `/health` para a API, e um deploy serve o build do web atrás da
 * mesma origem — então não há URL base, não há CORS, e o cookie de sessão é
 * primeira parte, que o navegador manda sem cerimônia. ADR-0012. É o que este
 * arquivo faz quando ninguém chama `configureApi`.
 *
 * O app do celular (ADR-0047) não tem origem: chama `configureApi` uma vez, ao
 * abrir, com o endereço da API e o token Bearer. O resto — prazos, a gravação
 * repetida que vira uma só, a leitura dos erros — é o mesmo para os dois.
 *
 * As respostas são lidas com os contratos Zod compartilhados em vez de
 * convertidas com `as`, para que um servidor que mude de forma quebre alto
 * aqui em vez de renderizar `undefined` três componentes adiante. Quem faz o
 * `.parse()` é cada módulo de família; este arquivo devolve `unknown` de
 * propósito, para que não exista caminho que pule a validação.
 *
 * `request` é exportado para os módulos vizinhos, e SÓ para eles: a fachada
 * `apps/web/src/lib/api.ts` reexporta os três erros e não ele.
 */
export class ApiUnreachableError extends Error {
    constructor(cause) {
        super('A API não respondeu.');
        this.name = 'ApiUnreachableError';
        this.cause = cause;
    }
}
/**
 * A request the server refused, carrying the stable `code` from the envelope.
 *
 * Callers branch on `code`, never on `message`: the message is written for a
 * person and may be reworded at any time.
 */
export class ApiError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
    }
}
/** Raised on 401 so a single handler can send the user back to the login screen. */
export class UnauthenticatedError extends ApiError {
    constructor(message = 'Sua sessão expirou.') {
        super(401, 'unauthenticated', message);
        this.name = 'UnauthenticatedError';
    }
}
let config = {};
/** Uma vez, ao abrir o app. O site nunca chama: o padrão é o site. */
export const configureApi = (next) => {
    config = next;
};
/** O fuso das perguntas que dependem do dia: o configurado, ou o do aparelho. */
export const clientTimeZone = () => {
    if (config.timeZone !== undefined)
        return config.timeZone;
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    catch {
        return 'America/Sao_Paulo';
    }
};
/**
 * O pedido que quem chamou desistiu de fazer.
 *
 * Pelo nome, e não por `instanceof DOMException`: o Hermes, que roda o app do
 * celular, não garante o `DOMException`, e o `fetch` dele cancela com um
 * `Error` de nome `AbortError`.
 */
const isAbort = (error) => error instanceof Error && error.name === 'AbortError';
/**
 * As gravações a caminho, pela chave método + endereço + corpo.
 *
 * O dono: *"se clica 3x no botão de criar, cria 3x"*. O botão fica ocupado só
 * depois que a tela redesenha, e três cliques rápidos (ou o Enter segurado)
 * chegam antes disso — em TODA tela que cria alguma coisa. Resolver aqui, uma
 * vez, em vez de em cada formulário: enquanto a mesma gravação ainda não
 * voltou, quem pedir de novo recebe a MESMA resposta, e o servidor recebe um
 * pedido só.
 *
 * Só o idêntico: dois pedidos diferentes seguem cada um o seu caminho. E só
 * enquanto estiver a caminho: depois da resposta, clicar de novo grava de
 * novo, que é o que a pessoa quer quando repete de propósito. `FormData`
 * (upload) fica de fora — não tem como comparar dois arquivos pelo texto.
 */
const inFlight = new Map();
export const request = async (path, options = {}) => {
    const { method = 'GET', body } = options;
    if (method === 'GET' || body instanceof FormData) {
        return await send(path, options);
    }
    const key = `${method} ${path} ${JSON.stringify(body ?? null)}`;
    const pending = inFlight.get(key);
    if (pending !== undefined)
        return await pending;
    const started = send(path, options).finally(() => {
        inFlight.delete(key);
    });
    inFlight.set(key, started);
    return await started;
};
/**
 * Quanto um pedido pode esperar antes de desistir (F212).
 *
 * Sem teto, um pedido que o caminho perdeu (a conexão morta de um balcão
 * depois de uma queda) ficava "carregando" até alguém dar F5. Leitura 30 s,
 * gravação 60 s, envio de arquivo 10 min.
 */
export const REQUEST_TIMEOUT_MS = {
    read: 30_000,
    write: 60_000,
    upload: 600_000,
};
const timeoutFor = (method, body) => body instanceof FormData
    ? REQUEST_TIMEOUT_MS.upload
    : method === 'GET'
        ? REQUEST_TIMEOUT_MS.read
        : REQUEST_TIMEOUT_MS.write;
const send = async (path, { method = 'GET', body, signal }) => {
    let response;
    const token = config.getToken?.() ?? null;
    // O pedido termina no que vier primeiro: quem chamou desistir, ou o teto.
    const controller = new AbortController();
    const deadline = { passed: false };
    const timer = setTimeout(() => {
        deadline.passed = true;
        controller.abort();
    }, timeoutFor(method, body));
    const forward = () => {
        controller.abort();
    };
    if (signal?.aborted === true)
        controller.abort();
    signal?.addEventListener('abort', forward);
    try {
        response = await fetch(`${config.baseUrl ?? ''}${path}`, {
            method,
            // Same-origin, so the cookie travels by default — stated explicitly
            // because a future change of origin would otherwise break sessions
            // silently. With a Bearer token there is no cookie to send.
            credentials: token === null ? 'same-origin' : 'omit',
            headers: {
                accept: 'application/json',
                ...(token === null ? {} : { authorization: `Bearer ${token}` }),
                ...(body === undefined || body instanceof FormData
                    ? {}
                    : { 'content-type': 'application/json' }),
            },
            ...(body === undefined
                ? {}
                : { body: body instanceof FormData ? body : JSON.stringify(body) }),
            signal: controller.signal,
        });
    }
    catch (error) {
        clearTimeout(timer);
        signal?.removeEventListener('abort', forward);
        if (deadline.passed)
            throw new ApiUnreachableError(new Error('timeout'));
        // An aborted request is the caller changing its mind, not a failure.
        if (isAbort(error))
            throw error;
        throw new ApiUnreachableError(error);
    }
    // O corpo também conta no teto: uma resposta cortada no meio não pendura.
    let text;
    try {
        text = response.status === 204 ? '' : await response.text();
    }
    catch (error) {
        if (deadline.passed || !isAbort(error)) {
            throw new ApiUnreachableError(error);
        }
        throw error;
    }
    finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', forward);
    }
    if (response.ok) {
        if (response.status === 204)
            return null;
        try {
            return JSON.parse(text);
        }
        catch (error) {
            // Um 200 que não é JSON inteiro é resposta cortada, não do servidor.
            throw new ApiUnreachableError(error);
        }
    }
    const parsed = ErrorResponse.safeParse((() => {
        try {
            return JSON.parse(text);
        }
        catch {
            return null;
        }
    })());
    const code = parsed.success ? parsed.data.code : 'request_failed';
    const message = parsed.success ? parsed.data.message : 'A requisição falhou.';
    /*
     * O servidor não foi alcançado — mesmo que a resposta seja HTTP (F197).
     *
     * No aplicativo instalado quem responde é o proxy local, e ele diz "a nuvem
     * não atendeu" com um 503 `server_unreachable` (ou 504 `server_timeout`), e
     * não com a conexão recusada. Tratado como erro comum, o app aberto sem
     * internet caía no login e o PDV nunca passava ao Edge. O mesmo vale para o
     * 502/504 cru de um gateway, que não é resposta da API.
     */
    if (code === 'server_unreachable' ||
        code === 'server_timeout' ||
        // F212: 404 e 503 sem o envelope da API também são o caminho, não ela —
        // o nginx durante um deploy. Um 404 da própria API traz o envelope.
        ([404, 502, 503, 504].includes(response.status) && !parsed.success)) {
        throw new ApiUnreachableError(new Error(`${String(response.status)} ${code}`));
    }
    if (response.status === 401) {
        if (code === 'unauthenticated')
            config.onUnauthorized?.(token);
        throw new UnauthenticatedError(message);
    }
    throw new ApiError(response.status, code, message);
};
//# sourceMappingURL=client.js.map